  import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Image,
  Input,
  message,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  EditOutlined,
  EyeOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import moment from 'moment';
import { subscriptionAPI } from '../../utils/api';

const { Title, Text } = Typography;

const statusColor = {
  pending_verification: 'warning',
  approved: 'success',
  rejected: 'error',
  trial: 'processing',
  active: 'success',
  expired: 'error',
};

const SubscriptionManagement = () => {
  const [requests, setRequests] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [rejectingRecord, setRejectingRecord] = useState(null);
  const [editingRecord, setEditingRecord] = useState(null);
  const [viewingPhoto, setViewingPhoto] = useState(null);
  const [rejectForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://qrwellness.jyada.in/api';
  const BASE_URL = API_BASE_URL.replace('/api', ''); // Remove /api to get base domain
  
  // Debug: Log the URLs being used
  console.log('=== URL DEBUG ===');
  console.log('process.env.REACT_APP_API_URL:', process.env.REACT_APP_API_URL);
  console.log('API_BASE_URL:', API_BASE_URL);
  console.log('BASE_URL:', BASE_URL);
  console.log('================');

  const fetchData = async () => {
    try {
      setLoading(true);
      const [requestsResponse, historyResponse] = await Promise.all([
        subscriptionAPI.getPaymentRequests(),
        subscriptionAPI.getHistory(),
      ]);
      setRequests(requestsResponse.data.requests || []);
      setHistory(historyResponse.data.subscriptions || []);
    } catch (error) {
      message.error('Failed to fetch subscription data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const approveRequest = (record) => {
    Modal.confirm({
      title: 'Approve Subscription Payment',
      content: `Approve payment ${record.request_id} for ${record.center_name}? This will activate the center subscription.`,
      okText: 'Approve',
      onOk: async () => {
        try {
          setReviewing(true);
          await subscriptionAPI.approvePaymentRequest(record.request_id);
          message.success('Subscription approved');
          fetchData();
        } catch (error) {
          message.error(error.response?.data?.error || 'Failed to approve payment');
        } finally {
          setReviewing(false);
        }
      },
    });
  };

  const rejectRequest = async (values) => {
    if (!rejectingRecord) return;

    try {
      setReviewing(true);
      await subscriptionAPI.rejectPaymentRequest(rejectingRecord.request_id, values);
      message.success('Payment request rejected');
      setRejectingRecord(null);
      rejectForm.resetFields();
      fetchData();
    } catch (error) {
      message.error(error.response?.data?.error || 'Failed to reject payment');
    } finally {
      setReviewing(false);
    }
  };

  const editSubscription = (record) => {
    setEditingRecord(record);
    editForm.setFieldsValue({
      status: record.status,
      expires_at: dayjs(record.expires_at),
    });
  };

  const handleEditSubmit = async (values) => {
    if (!editingRecord) return;

    try {
      setReviewing(true);
      const payload = {
        status: values.status,
        expires_at: values.expires_at ? values.expires_at.format('YYYY-MM-DD') : undefined,
      };

      await subscriptionAPI.updateSubscription(editingRecord.id, payload);
      message.success('Subscription updated successfully');
      setEditingRecord(null);
      editForm.resetFields();
      fetchData();
    } catch (error) {
      message.error(error.response?.data?.error || 'Failed to update subscription');
    } finally {
      setReviewing(false);
    }
  };

  const requestColumns = [
    {
      title: 'Request',
      dataIndex: 'request_id',
      key: 'request_id',
      render: (value, record) => (
        <div>
          <Text strong>{value}</Text>
          <div style={{ fontSize: 12, color: '#64748b' }}>
            {moment(record.created_at).format('MMM DD, YYYY HH:mm')}
          </div>
        </div>
      ),
    },
    {
      title: 'Center',
      key: 'center',
      render: (_, record) => (
        <div>
          <Text strong>{record.center_name}</Text>
          <div style={{ fontSize: 12, color: '#64748b' }}>
            {record.admin_name} - {record.admin_email}
          </div>
        </div>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount, record) => (
        <div>
          <Text strong>Rs. {new Intl.NumberFormat('en-IN').format(Number(amount || 0))}</Text>
          {record.member_count !== undefined && (
            <div style={{ fontSize: 11, color: '#64748b' }}>
              {record.member_count} members
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'UTR',
      dataIndex: 'upi_transaction_id',
      key: 'upi_transaction_id',
      render: (value) => <Text copyable>{value}</Text>,
    },
    {
      title: 'Payment Proof',
      dataIndex: 'payment_proof_photo',
      key: 'payment_proof_photo',
      render: (photoPath, record) => {
        if (!photoPath) {
          return <Text type="secondary" style={{ fontSize: 11 }}>No photo</Text>;
        }
        
        // Construct full URL for the image
        let photoUrl;
        if (photoPath.startsWith('http')) {
          photoUrl = photoPath;
        } else {
          // photoPath should be like "/uploads/payment-proofs/filename.jpg"
          photoUrl = `${BASE_URL}${photoPath}`;
        }
        
        console.log('Photo URL:', photoUrl, 'Path:', photoPath); // Debug log
        
        return (
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => setViewingPhoto({ url: photoUrl, record })}
          >
            View
          </Button>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={statusColor[status] || 'default'}>
          {status?.replace(/_/g, ' ').toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        record.status === 'pending_verification' ? (
          <Space>
            <Button
              type="primary"
              size="small"
              icon={<CheckOutlined />}
              loading={reviewing}
              onClick={() => approveRequest(record)}
            >
              Approve
            </Button>
            <Button
              danger
              size="small"
              icon={<CloseOutlined />}
              onClick={() => setRejectingRecord(record)}
            >
              Reject
            </Button>
          </Space>
        ) : (
          <Text type="secondary">Reviewed</Text>
        )
      ),
    },
  ];

  const historyColumns = [
    {
      title: 'Center',
      dataIndex: 'center_name',
      key: 'center_name',
    },
    {
      title: 'Plan',
      dataIndex: 'plan_name',
      key: 'plan_name',
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => `Rs. ${new Intl.NumberFormat('en-IN').format(Number(amount || 0))}`,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => <Tag color={statusColor[status] || 'default'}>{status?.toUpperCase()}</Tag>,
    },
    {
      title: 'Start',
      dataIndex: 'starts_at',
      key: 'starts_at',
      render: (date) => moment(date).format('MMM DD, YYYY'),
    },
    {
      title: 'Expiry',
      dataIndex: 'expires_at',
      key: 'expires_at',
      render: (date) => {
        const expiryDate = moment(date);
        const daysLeft = expiryDate.diff(moment(), 'days');
        const isExpired = daysLeft < 0;
        return (
          <div>
            <div>{expiryDate.format('MMM DD, YYYY')}</div>
            <Text type={isExpired ? 'danger' : 'success'} style={{ fontSize: 11 }}>
              {isExpired ? `Expired ${Math.abs(daysLeft)} days ago` : `${daysLeft} days left`}
            </Text>
          </div>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          icon={<EditOutlined />}
          onClick={() => editSubscription(record)}
        >
          Edit
        </Button>
      ),
    },
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>Subscriptions</Title>
        </Col>
        <Col>
          <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>
            Refresh
          </Button>
        </Col>
      </Row>

      <Card>
        <Tabs
          items={[
            {
              key: 'requests',
              label: 'Payment Requests',
              children: (
                <div style={{ 
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  maxWidth: '100%',
                  borderRadius: '4px'
                }}>
                  <Table
                    columns={requestColumns}
                    dataSource={requests}
                    rowKey="id"
                    loading={loading}
                    pagination={{ pageSize: 20 }}
                    scroll={{ x: 1000 }}
                    style={{ minWidth: 'max-content' }}
                  />
                </div>
              ),
            },
            {
              key: 'history',
              label: 'Subscription History',
              children: (
                <div style={{ 
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  maxWidth: '100%',
                  borderRadius: '4px'
                }}>
                  <Table
                    columns={historyColumns}
                    dataSource={history}
                    rowKey="id"
                    loading={loading}
                    pagination={{ pageSize: 20 }}
                    scroll={{ x: 900 }}
                    style={{ minWidth: 'max-content' }}
                  />
                </div>
              ),
            },
          ]}
        />
      </Card>

      {/* Reject Modal */}
      <Modal
        title="Reject Payment Request"
        open={!!rejectingRecord}
        onCancel={() => {
          setRejectingRecord(null);
          rejectForm.resetFields();
        }}
        footer={null}
      >
        <Form form={rejectForm} layout="vertical" onFinish={rejectRequest}>
          <Form.Item name="reason" label="Reason">
            <Input.TextArea
              rows={4}
              placeholder="Tell the center admin why the payment could not be verified"
            />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setRejectingRecord(null)}>Cancel</Button>
              <Button danger type="primary" htmlType="submit" loading={reviewing}>
                Reject Payment
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Subscription Modal */}
      <Modal
        title="Edit Subscription"
        open={!!editingRecord}
        onCancel={() => {
          setEditingRecord(null);
          editForm.resetFields();
        }}
        footer={null}
      >
        {editingRecord && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ marginBottom: 12 }}>
              <Text strong>Center: </Text>
              <Text>{editingRecord.center_name}</Text>
            </div>
            <div style={{ marginBottom: 12 }}>
              <Text strong>Plan: </Text>
              <Text>{editingRecord.plan_name}</Text>
            </div>
            <div style={{ marginBottom: 20 }}>
              <Text strong>Amount: </Text>
              <Text>Rs. {new Intl.NumberFormat('en-IN').format(Number(editingRecord.amount || 0))}</Text>
            </div>

            <Form form={editForm} layout="vertical" onFinish={handleEditSubmit}>
              <Form.Item
                name="status"
                label="Status"
                rules={[{ required: true, message: 'Please select status' }]}
              >
                <Select
                  options={[
                    { label: 'Trial', value: 'trial' },
                    { label: 'Active', value: 'active' },
                    { label: 'Expired', value: 'expired' },
                  ]}
                />
              </Form.Item>

              <Form.Item
                name="expires_at"
                label="Expiry Date"
                rules={[{ required: true, message: 'Please select expiry date' }]}
              >
                <DatePicker format="YYYY-MM-DD" />
              </Form.Item>

              <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                <Space>
                  <Button onClick={() => setEditingRecord(null)}>Cancel</Button>
                  <Button type="primary" htmlType="submit" loading={reviewing}>
                    Update Subscription
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>

      {/* View Payment Proof Modal */}
      <Modal
        title="Payment Proof"
        open={!!viewingPhoto}
        onCancel={() => setViewingPhoto(null)}
        footer={[
          <Button key="close" onClick={() => setViewingPhoto(null)}>
            Close
          </Button>,
        ]}
        width={700}
        centered
      >
        {viewingPhoto && (
          <div>
            <div style={{ marginBottom: 16, padding: 12, background: '#f8fafc', borderRadius: 8 }}>
              <Row gutter={[16, 8]}>
                <Col span={12}>
                  <Text type="secondary" style={{ fontSize: 12 }}>Center:</Text>
                  <div><Text strong>{viewingPhoto.record?.center_name || 'N/A'}</Text></div>
                </Col>
                <Col span={12}>
                  <Text type="secondary" style={{ fontSize: 12 }}>Amount:</Text>
                  <div><Text strong>₹{new Intl.NumberFormat('en-IN').format(Number(viewingPhoto.record?.amount || 0))}</Text></div>
                </Col>
                <Col span={12}>
                  <Text type="secondary" style={{ fontSize: 12 }}>UTR:</Text>
                  <div><Text copyable style={{ fontSize: 12 }}>{viewingPhoto.record?.upi_transaction_id || 'N/A'}</Text></div>
                </Col>
                <Col span={12}>
                  <Text type="secondary" style={{ fontSize: 12 }}>Submitted:</Text>
                  <div><Text style={{ fontSize: 12 }}>{viewingPhoto.record?.created_at ? moment(viewingPhoto.record.created_at).format('MMM DD, YYYY HH:mm') : 'N/A'}</Text></div>
                </Col>
              </Row>
            </div>
            
            {/* Debug info - remove after testing */}
            {/* <div style={{ marginBottom: 8, padding: 8, background: '#fff3cd', fontSize: 11, borderRadius: 4 }}>
              <strong>Debug:</strong> {viewingPhoto.url}
            </div> */}
            
            <div style={{ 
              textAlign: 'center',
              border: '1px solid #e2e8f0',
              borderRadius: 8,
              padding: 16,
              background: '#fff'
            }}>
              <Image
                src={viewingPhoto.url}
                alt="Payment proof"
                style={{ maxWidth: '100%', maxHeight: '500px', objectFit: 'contain' }}
                fallback="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dy='.3em' fill='%23999' font-size='16'%3EImage not available%3C/text%3E%3C/svg%3E"
                onError={(e) => {
                  console.error('Image load error:', viewingPhoto.url);
                  console.error('Error event:', e);
                }}
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SubscriptionManagement;
